import { Link, useNavigate } from "@tanstack/react-router";
import type { ComponentProps } from "react";

/**
 * The data table's state lives in the current page's search params (`validateSearch` with the
 * contract's list input). The table is shared by every list page, so it cannot know the route's
 * own search type; these two helpers are the one place that updates "whatever page we are on",
 * keeping every other parameter as it is.
 */

export type SearchParams = Record<string, unknown>;
export type SearchUpdate = (previous: SearchParams) => SearchParams;

/** A link to the current page with other search params: sort headers, pagination, "clear filters". */
export function SearchLink({
	search,
	...props
}: Omit<ComponentProps<"a">, "href"> & { search: SearchUpdate }) {
	return <Link to="." search={search as never} {...props} />;
}

/** Sets search params on the current page from code (a search box, a filter select). */
export function useSetSearch() {
	const navigate = useNavigate();
	return (search: SearchUpdate, options: { readonly replace?: boolean } = {}) =>
		navigate({ to: ".", search: search as never, replace: options.replace });
}

/** Changing what is shown starts again at the first page. */
export const firstPage = (search: SearchParams): SearchParams => ({ ...search, page: undefined });
